// src/components/antenna/antenna-brand-associations.tsx
import React from "react";
import { ChevronDown, ChevronUp, Sparkles, TrendingUp, TrendingDown } from "lucide-react";
import { Card } from "../ui/card";
import { TooltipIcon } from "../tooltip-icon";
import { ModelFilterTabs } from "../model-filter-tabs";
import { normalizeProviderKey } from "@/app/constants/models";
import { stripCitations } from "@/app/utils/strip-citations";
import { SignalEntry, toSignalItem } from "@/app/utils/signal-items";

// ─── Types ────────────────────────────────────────────────────────────────────

interface PerceptionData {
  brandAssociations: string[];
  strengths:         SignalEntry[];
  weaknesses:        SignalEntry[];
  quote:             string;
  [key: string]: any;
}

interface PerModelPerception {
  brandAssociations?: string[];
  strengths?:         SignalEntry[];
  weaknesses?:        SignalEntry[];
  quote?:             string;
}

interface ThemeItem {
  theme:       string;
  sentiment:   "positive" | "negative" | "neutral";
  impactScore: number;
  description: string;
}

// A single row in the working-for / working-against columns. `impact` and
// `description` are present for items that came from a detected theme, absent
// for items folded in from the plain strengths/weaknesses lists.
interface MergedItem {
  label:        string;
  impact?:      number;
  description?: string;
}

interface AntennaBrandAssociationsProps {
  perceptionData: PerceptionData;
  perceptionDataByModel?: Record<string, PerModelPerception>;
  /** Detected themes (combined) — the spine of the working-for/against columns. */
  themes?: ThemeItem[];
  /** Per-model snapshot data; we read `.detectedThemes` off the selected model. */
  themesByModel?: Record<string, { detectedThemes?: ThemeItem[] }>;
  modelTabs?: { value: string; label: string }[];
  selectedModel?: string;
  onModelChange?: (model: string) => void;
  isExpanded: boolean;
  toggleExpanded: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function ModelSourceBadge({ label }: { label: string }) {
  return (
    <span
      style={{
        display:         "inline-flex",
        alignItems:      "center",
        padding:         "2px 8px",
        borderRadius:    9999,
        backgroundColor: "var(--alpha-teal-10)",
        border:          "1px solid var(--alpha-teal-20)",
        color:           "var(--alpha-teal)",
        fontSize:        "var(--text-xs)",
        fontWeight:      600,
        marginLeft:      6,
        flexShrink:      0,
      }}
    >
      {label}
    </span>
  );
}

function MergedBadge() {
  return (
    <span
      style={{
        display:         "inline-flex",
        alignItems:      "center",
        padding:         "1px 7px",
        borderRadius:    9999,
        backgroundColor: "var(--muted)",
        border:          "1px solid var(--border)",
        color:           "var(--muted-foreground)",
        fontSize:        "10px",
        fontWeight:      500,
        marginLeft:      6,
        flexShrink:      0,
      }}
    >
      All models
    </span>
  );
}

const normKey = (s: string) => s.toLowerCase().replace(/[^a-z0-9]+/g, " ").trim();

// Fold the strengths/weaknesses entries onto the themed items, dropping any
// entry already captured by a theme label so nothing reads twice. Entries are
// normalized through toSignalItem (structured {label, description} pairs
// pass through; bare strings stay bare labels, never dressed up with a
// derived description).
function mergeList(themeItems: MergedItem[], listItems: SignalEntry[]): MergedItem[] {
  const labels = themeItems.map((t) => normKey(t.label)).filter(Boolean);
  const extra: MergedItem[] = [];
  for (const entry of listItems || []) {
    const item = toSignalItem(entry);
    if (!item) continue;
    const n = normKey(item.label);
    if (!n) continue;
    if (labels.some((l) => l.includes(n) || n.includes(l))) continue;
    extra.push({ label: item.label, description: item.description });
  }
  return [...themeItems, ...extra];
}

const COLUMN_PREVIEW = 4;

// ─── Working-for / working-against column ──────────────────────────────────────
//
// Card-pair contract (operator spec 7/19, richness ruling 7/20):
// 1. Both cards are ALWAYS the same height (grid stretch, flex column).
// 2. Rich items (scored themes, or genuine model-written {label,
//    description} pairs) render bold title + regular description in the
//    main tier. Thin items (bare one-liners) render as plain body-weight
//    lines under the "Also noted" fallback divider, which only appears
//    when thin items exist. Structure comes from the DATA layer and is
//    never derived: see toSignalItem (signal-items.ts).
// 3. The two cards collapse and expand TOGETHER (shared open state lifted to
//    the parent), so the "N more" buttons always sit pinned side by side on
//    the same bottom line and one column can never be open while the other
//    is truncated.

function StoryColumn({
  title,
  accent,
  direction,
  items,
  badge,
  open,
  onToggle,
}: {
  title: string;
  accent: string;
  direction: "up" | "down";
  items: MergedItem[];
  badge: React.ReactNode;
  open: boolean;
  onToggle: () => void;
}) {
  // Two tiers by RICHNESS. "Also noted" is the fallback for thin data:
  // items where the model gave only a one-liner. Anything rich renders in
  // the main tier, whether it came from a scored theme (impact) or from the
  // strengths/weaknesses list as a genuine {label, description} pair. With
  // the derived-description splitter removed, a description now always
  // means the model actually wrote one, so shape is an honest signal again.
  // A run where the model made everything rich shows no divider at all.
  const detailed = items.filter((i) => i.impact !== undefined || !!i.description);
  const topline  = items.filter((i) => i.impact === undefined && !i.description);
  const ordered  = [...detailed, ...topline];
  const visible  = open ? ordered : ordered.slice(0, COLUMN_PREVIEW);
  const overflow = ordered.length - COLUMN_PREVIEW;
  const Icon = direction === "up" ? TrendingUp : TrendingDown;

  return (
    <div
      className="rounded-xl p-4 flex flex-col"
      style={{ backgroundColor: "var(--muted)", border: "1px solid var(--border)", borderLeft: `3px solid ${accent}` }}
    >
      <div className="flex items-center gap-2 mb-3">
        <Icon className="w-3.5 h-3.5" style={{ color: accent }} />
        <h4 style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--text-sm)", color: accent }}>
          {title}
        </h4>
        {badge}
        <span style={{ marginLeft: "auto", fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
          {items.length} total
        </span>
        {/* When expanded, the collapse control also lives up here: on a long
            expanded list the bottom button is a full scroll away. */}
        {open && overflow > 0 && (
          <button onClick={onToggle} className="flex items-center gap-1"
            style={{ fontSize: "var(--text-xs)", color: "#28a9d2", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            <ChevronUp className="w-3 h-3" /> Show less
          </button>
        )}
      </div>

      <div className="space-y-2.5">
        {items.length === 0 && (
          <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>No data available.</p>
        )}
        {visible.map((item, i) => (
          <React.Fragment key={i}>
            {/* The qualifier renders wherever the loose-list tier begins,
                INCLUDING index 0: a column with zero scored themes is all
                loose items and still needs the label, otherwise less-vetted
                content reads as top-tier. */}
            {i === detailed.length && topline.length > 0 && (
              <div
                className="flex items-center gap-2"
                style={i === 0
                  ? { marginBottom: 2 }
                  : { marginTop: 6, paddingTop: 10, borderTop: "1px dashed var(--border)" }}
              >
                <span style={{ fontSize: "10px", fontWeight: 600, letterSpacing: "0.06em", textTransform: "uppercase", color: "var(--muted-foreground)" }}>
                  Also noted
                </span>
                <span style={{ fontSize: "10px", color: "var(--muted-foreground)" }}>{topline.length}</span>
              </div>
            )}
            <div className="flex items-start gap-2">
              <div className="mt-1.5 w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ backgroundColor: accent }} />
              <div className="flex-1 min-w-0">
                {/* Weight follows the tier: rich items read as bold headline
                    plus description; thin "Also noted" items always read as
                    plain body text, whatever their length. */}
                <span style={item.impact === undefined && !item.description
                  ? { fontSize: "var(--text-sm)", color: "var(--foreground)", lineHeight: "1.5", fontWeight: 400 }
                  : { fontSize: "var(--text-sm)", color: "var(--foreground)", lineHeight: "1.4", fontWeight: 500 }}>
                  {stripCitations(item.label)}
                </span>
                {item.description && (
                  <p style={{ fontSize: "var(--text-xs)", color: "var(--muted-foreground)", lineHeight: "1.5", marginTop: 2 }}>
                    {stripCitations(item.description)}
                  </p>
                )}
              </div>
            </div>
          </React.Fragment>
        ))}
      </div>

      {overflow > 0 && (
        <button onClick={onToggle} className="flex items-center gap-1 mt-auto"
          style={{ fontSize: "var(--text-xs)", color: "#28a9d2", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0, paddingTop: 10 }}>
          {open ? <><ChevronUp className="w-3 h-3" /> Show less</> : <><ChevronDown className="w-3 h-3" /> {overflow} more</>}
        </button>
      )}
    </div>
  );
}

// ─── Component ────────────────────────────────────────────────────────────────

export function AntennaBrandAssociations({
  perceptionData,
  perceptionDataByModel = {},
  themes = [],
  themesByModel = {},
  modelTabs = [],
  selectedModel = "all",
  onModelChange,
  isExpanded,
  toggleExpanded,
}: AntennaBrandAssociationsProps) {
  const isAll = selectedModel === "all";

  const [assocOpen, setAssocOpen] = React.useState(false);
  // Shared expansion for the working-for/against card pair: both columns
  // collapse and expand together so their buttons stay on one baseline.
  const [storyOpen, setStoryOpen] = React.useState(false);
  // New data collapses the pair: an expanded state left over from the
  // previous run should not carry into a fresh run's view.
  React.useEffect(() => {
    setStoryOpen(false);
  }, [perceptionData]);

  // Merge per-model data over the combined base where a model is selected
  const active = React.useMemo((): PerceptionData => {
    if (isAll) return perceptionData;
    const key      = normalizeProviderKey(selectedModel);
    const modelRaw = perceptionDataByModel[key];
    if (!modelRaw) return perceptionData;
    return {
      ...perceptionData,
      brandAssociations: modelRaw.brandAssociations ?? perceptionData.brandAssociations,
      strengths:         modelRaw.strengths         ?? perceptionData.strengths,
      weaknesses:        modelRaw.weaknesses        ?? perceptionData.weaknesses,
      quote:             modelRaw.quote             ?? perceptionData.quote,
    };
  }, [isAll, selectedModel, perceptionDataByModel, perceptionData]);

  // Themes for the active model (fall back to the combined set)
  const activeThemes: ThemeItem[] = React.useMemo(() => {
    if (isAll) return themes;
    const key = normalizeProviderKey(selectedModel);
    return themesByModel[key]?.detectedThemes ?? themes;
  }, [isAll, selectedModel, themes, themesByModel]);

  // Build the two columns: themed items first (sorted by impact), then any
  // strengths/weaknesses not already named by a theme.
  const { positives, negatives } = React.useMemo(() => {
    const themed = (sentiment: "positive" | "negative"): MergedItem[] =>
      activeThemes
        .filter((t) => t.sentiment === sentiment)
        .sort((a, b) => (b.impactScore ?? 0) - (a.impactScore ?? 0))
        .map((t) => ({ label: t.theme, impact: t.impactScore, description: t.description }));
    return {
      positives: mergeList(themed("positive"), active.strengths || []),
      negatives: mergeList(themed("negative"), active.weaknesses || []),
    };
  }, [activeThemes, active.strengths, active.weaknesses]);

  const modelLabel = isAll
    ? null
    : (modelTabs.find((t) => t.value === selectedModel)?.label ?? selectedModel);

  const hasPerModelData =
    Object.keys(perceptionDataByModel).length > 0 && modelTabs.length > 0;

  const columnBadge = isAll ? <MergedBadge /> : (modelLabel ? <ModelSourceBadge label={modelLabel} /> : null);

  return (
    <>
      <Card style={{ backgroundColor: "var(--card)", borderColor: "var(--border)" }}>
        <div className="p-6 space-y-8">

          {/* ── Header ──────────────────────────────────────────────────── */}
          <div
            className="flex items-center justify-between cursor-pointer"
            onClick={toggleExpanded}
            style={{ userSelect: "none" }}
          >
            <div>
              <div className="flex items-center gap-2 flex-wrap">
                <h3 style={{ fontSize: "var(--text-lg)", fontWeight: "var(--font-weight-semibold)" }}>
                  How AI Describes Your Brand
                </h3>
                {!isAll && modelLabel && <ModelSourceBadge label={modelLabel} />}
              </div>
              <p style={{ color: "var(--muted-foreground)", marginTop: "4px", fontSize: "var(--text-sm)" }}>
                {isAll
                  ? "The markers AI ties to your brand, and what's working for and against you."
                  : `${modelLabel}'s read — switch to "All Models" for the merged view`}
              </p>
            </div>
            <div className="flex items-center" style={{ gap: 12, flexShrink: 0 }}>
              {hasPerModelData && (
                <ModelFilterTabs
                  models={modelTabs}
                  selectedModel={selectedModel}
                  onModelChange={onModelChange ?? (() => {})}
                  combinedLabel="All Models"
                  includeCombined={true}
                />
              )}
              {isExpanded
                ? <ChevronUp   className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />
                : <ChevronDown className="w-5 h-5" style={{ color: "var(--muted-foreground)" }} />}
            </div>
          </div>

          {isExpanded && (
            <div className="space-y-6">

              {/* ── Brand Associations — pill chips full width ───────── */}
              {(() => {
                const items    = active.brandAssociations || [];
                const visible  = assocOpen ? items : items.slice(0, 8);
                const overflow = items.length - 8;
                return (
                  <div>
                    <div className="flex items-center gap-2 mb-3">
                      <h4 style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--text-sm)" }}>
                        Brand Markers
                      </h4>
                      {isAll ? <MergedBadge /> : modelLabel && <ModelSourceBadge label={modelLabel} />}
                      <span style={{ marginLeft: "auto", fontSize: "var(--text-xs)", color: "var(--muted-foreground)" }}>
                        {items.length} total
                      </span>
                    </div>
                    {items.length === 0
                      ? <p style={{ fontSize: "var(--text-sm)", color: "var(--muted-foreground)" }}>No data available.</p>
                      : (
                        <div className="flex flex-wrap gap-2">
                          {visible.map((item: string, i: number) => (
                            <span
                              key={i}
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                padding: "4px 12px",
                                borderRadius: 9999,
                                backgroundColor: "var(--muted)",
                                border: "1px solid var(--border)",
                                borderLeft: "3px solid #28a9d2",
                                fontSize: "var(--text-xs)",
                                fontWeight: 500,
                                color: "var(--foreground)",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {item}
                            </span>
                          ))}
                        </div>
                      )
                    }
                    {overflow > 0 && (
                      <button onClick={() => setAssocOpen(!assocOpen)} className="flex items-center gap-1 mt-2"
                        style={{ fontSize: "var(--text-xs)", color: "#28a9d2", fontWeight: 600, background: "none", border: "none", cursor: "pointer", padding: 0 }}>
                        {assocOpen ? <><ChevronUp className="w-3 h-3" /> Show less</> : <><ChevronDown className="w-3 h-3" /> {overflow} more</>}
                      </button>
                    )}
                  </div>
                );
              })()}

              {/* ── Divider ─────────────────────────────────────────── */}
              <div style={{ borderTop: "1px solid var(--border)" }} />

              {/* ── Working for you / against you (themes + strengths/weaknesses) ── */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <StoryColumn
                  title="What's working for you"
                  accent="var(--alpha-teal)"
                  direction="up"
                  items={positives}
                  badge={columnBadge}
                  open={storyOpen}
                  onToggle={() => setStoryOpen(!storyOpen)}
                />
                <StoryColumn
                  title="What's working against you"
                  accent="var(--infrared-coral)"
                  direction="down"
                  items={negatives}
                  badge={columnBadge}
                  open={storyOpen}
                  onToggle={() => setStoryOpen(!storyOpen)}
                />
              </div>

              {/* ── AI insight — same card, distinct tint ───────────── */}
              {active.quote && (
                <>
                  <div style={{ borderTop: "1px solid var(--border)" }} />
                  <div
                    className="rounded-xl p-4"
                    style={{
                      backgroundColor: "var(--alpha-violet-10, rgba(139,92,246,0.06))",
                      border:          "1px solid var(--border)",
                      borderLeft:      "3px solid var(--spectral-violet)",
                    }}
                  >
                    <div className="flex items-start gap-4">
                      <TooltipIcon
                        Icon={Sparkles}
                        tooltip="AI-generated insight"
                        size={20}
                        style={{ color: "var(--spectral-violet)", marginTop: "2px" }}
                      />
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h4 style={{ fontWeight: "var(--font-weight-semibold)", fontSize: "var(--text-sm)", color: "var(--spectral-violet)" }}>
                            How models understand your brand positioning
                          </h4>
                          {!isAll && modelLabel && <ModelSourceBadge label={modelLabel} />}
                        </div>
                        <p style={{ color: "var(--muted-foreground)", fontSize: "var(--text-sm)", lineHeight: "1.5" }}>
                          {active.quote}
                        </p>
                      </div>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </div>
      </Card>
    </>
  );
}
